import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// ✅ Use environment variable
const BASE_URL = process.env.REACT_APP_API_URL;

// 🔹 Fetch Loan EMI Schedule by LoanId
export const fetchLoanEmis = createAsyncThunk(
  "loanEmi/fetchLoanEmis",
  async (loanId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/get-loanemi/${loanId}`, // ✅ use BASE_URL
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data; // contains { message, count, emis[] }
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

// 🔹 Pay EMI
export const payLoanEmi = createAsyncThunk(
  "loanEmi/payLoanEmi",
  async ({ loanId, amountPaid, paymentMode, paymentDate }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/loan/pay-emi?loanId=${loanId}`, // ✅ use BASE_URL
        {
          amountPaid,
          paymentMode,
          paymentDate,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      return response.data; // expected { message, paidEMI, unusedAmount }
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

const loanEmiSlice = createSlice({
  name: "loanEmi",
  initialState: {
    emis: [],
    loanNumber: null,
    customerName: null,
    count: 0,
    loading: false,
    error: null,
    paymentLoading: false,
    paymentSuccess: null,
  },
  reducers: {
    clearLoanEmis: (state) => {
      state.emis = [];
      state.loanNumber = null;
      state.customerName = null;
      state.count = 0;
      state.error = null;
      state.loading = false;
      state.paymentLoading = false;
      state.paymentSuccess = null;
    },
    clearPaymentState: (state) => {
      state.paymentLoading = false;
      state.paymentSuccess = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch EMIs
      .addCase(fetchLoanEmis.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLoanEmis.fulfilled, (state, action) => {
        state.loading = false;
        state.emis = action.payload.emis || [];
        state.count = action.payload.count || 0;

        if (action.payload.emis?.length > 0) {
          state.customerName = action.payload.emis[0].customerName;
          state.loanNumber = action.payload.emis[0].loanNumber;
        }
      })
      .addCase(fetchLoanEmis.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : action.payload?.message || "Failed to fetch loan EMIs";
      })

      // Pay EMI
      .addCase(payLoanEmi.pending, (state) => {
        state.paymentLoading = true;
        state.paymentSuccess = null;
        state.error = null;
      })
      .addCase(payLoanEmi.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.paymentSuccess = action.payload.message || "EMI Paid Successfully ✅";

        // Optional: update EMI in state if backend returns updated EMI record
        if (action.payload.updatedEmi) {
          state.emis = state.emis.map((emi) =>
            emi.id === action.payload.updatedEmi.id ? action.payload.updatedEmi : emi
          );
        }
      })
      .addCase(payLoanEmi.rejected, (state, action) => {
        state.paymentLoading = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : action.payload?.message || "Failed to pay EMI";
      });
  },
});

export const { clearLoanEmis, clearPaymentState } = loanEmiSlice.actions;
export default loanEmiSlice.reducer;
